import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { COMPANY_LOGO_SRC } from '../../constants/branding';

const BLUE = '#101f78';
const RED = '#cf3e35';
const A4_WIDTH = '8.27in';
const A4_HEIGHT = '11.69in';

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Online Banking', 'Card', 'TNG / eWallet', 'Other'];

const formatYmd = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return new Date().toISOString().split('T')[0];
  return x.toISOString().split('T')[0];
};

/** Calendar date in local timezone (matches <input type="date" />). */
const toYmdLocal = (d) => {
  if (d == null || d === '') return formatYmd(new Date());
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return formatYmd(new Date());
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatRM = (value) => `RM ${Number(value || 0).toFixed(2)}`;

const receiptNo = (collection) => {
  const id = `${collection?._id || collection?.id || ''}`;
  const digits = id.replace(/\D/g, '').slice(-7).padStart(7, '0');
  return `RECPT#${digits}`;
};

/** PDF filename base: RECPT# + collection number */
const receiptPdfDownloadBase = (collection) => {
  const no = (collection?.collectionNo || '').toString().trim();
  if (no) return `RECPT#${no}`.replace(/[^\w#.-]+/g, '_');
  return (receiptNo(collection) || 'RECPT').replace(/[^\w#.-]+/g, '_');
};

const shouldIgnoreReceiptExportNode = (node) => {
  if (!node?.classList) return false;
  return (
    node.classList.contains('receipt-hide-print')
    || node.classList.contains('receipt-payment-editor-only')
  );
};

const normalizeContact = (person) => {
  const value = person?.mobiles;
  if (Array.isArray(value)) return value.filter(Boolean).join(' / ');
  if (typeof value === 'string') return value;
  return 'N/A';
};

const emptyPaymentRow = () => ({
  paymentDate: toYmdLocal(new Date()),
  paymentMethod: 'Cash',
  amount: '',
  reference: '',
});

const normalizePaymentRowFromApi = (p) => ({
  paymentDate: p?.paymentDate != null ? toYmdLocal(p.paymentDate) : toYmdLocal(new Date()),
  paymentMethod: p?.paymentMethod || 'Cash',
  amount: p?.amount != null && p.amount !== '' ? String(p.amount) : '',
  reference: p?.reference != null ? String(p.reference) : '',
});

const collectionRefMs = (collection) => {
  const raw = collection?.collectionDate ?? collection?.createdAt;
  if (raw == null) return Date.now();
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? Date.now() : d.getTime();
};

const Receipt = ({ collection, shipper, consignee: _consignee, onClose, onSaveReceipt, onPrint }) => {
  const receiptTo = shipper || {};
  const logoTriedFallback = useRef(false);
  const [logoSrc, setLogoSrc] = useState(COMPANY_LOGO_SRC);
  const [receiptIssuedDate, setReceiptIssuedDate] = useState(() => toYmdLocal(new Date()));
  const [paymentRows, setPaymentRows] = useState(() => [emptyPaymentRow()]);
  const [saveBusy, setSaveBusy] = useState(false);
  const rootRef = useRef(null);
  const downloadLock = useRef(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  const receiptPaymentsKey = useMemo(
    () => JSON.stringify(collection?.receiptPayments ?? null),
    [collection?.receiptPayments],
  );

  /* Sync from server when collection id or saved receipt data changes. */
  useEffect(() => {
    if (!collection) return;
    logoTriedFallback.current = false;
    setLogoSrc(COMPANY_LOGO_SRC);
    const issued = collection.receiptIssuedDate != null
      ? toYmdLocal(collection.receiptIssuedDate)
      : toYmdLocal(collectionRefMs(collection));
    setReceiptIssuedDate(issued);
    const rp = collection.receiptPayments;
    if (Array.isArray(rp) && rp.length > 0) {
      setPaymentRows(rp.map(normalizePaymentRowFromApi));
    } else {
      setPaymentRows([emptyPaymentRow()]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- receiptPaymentsKey + id fields; avoid resetting on unrelated collection prop churn
  }, [
    collection?._id,
    collection?.id,
    collection?.receiptIssuedDate,
    receiptPaymentsKey,
    collection?.collectionDate,
    collection?.createdAt,
  ]);

  const rows = useMemo(() => {
    if (collection?.boxes?.length) {
      return collection.boxes.map((box, idx) => {
        const qty = parseInt(box.quantity, 10) || 1;
        const price = parseFloat(box.price) || 0;
        return {
          no: idx + 1,
          itemType: box.boxType || '-',
          description: box.description || box.boxType || 'JET L BOX',
          qty,
          price,
          total: qty * price,
        };
      });
    }
    return [{ no: 1, itemType: 'JET L BOX', description: 'JET L BOX', qty: 1, price: 0, total: 0 }];
  }, [collection?.boxes]);

  const totals = useMemo(() => {
    const subTotal = rows.reduce((sum, row) => sum + row.total, 0);
    const ch = collection?.charges || {};
    const packagingCharge = Number.parseFloat(ch.packingCharge) || 0;
    const transport = Number.parseFloat(ch.transportCharge) || 0;
    const otherCharge = Number.parseFloat(ch.otherCharge) || 0;
    const discountType = ch.discountType === 'amount' ? 'amount' : 'percentage';
    const discountValue = Number.parseFloat(ch.discountValue) || 0;
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = subTotal * (discountValue / 100);
    } else {
      discountAmount = discountValue;
    }
    const total = subTotal + packagingCharge + transport + otherCharge - discountAmount;
    const discountLabel = discountType === 'percentage' ? `${discountValue}%` : formatRM(discountAmount);
    return {
      subTotal,
      packagingCharge,
      transport,
      otherCharge,
      discountPercent: discountType === 'percentage' ? discountValue : 0,
      discountLabel,
      discountAmount,
      total,
    };
  }, [rows, collection?.charges]);

  const { totalPaid, balance, paymentStatus } = useMemo(() => {
    const due = Number(totals.total) || 0;
    const paid = paymentRows.reduce((sum, r) => sum + (Number.parseFloat(r.amount) || 0), 0);
    const bal = Math.max(0, Math.round((due - paid) * 100) / 100);
    const eps = 0.005;
    let status = 'not_paid';
    if (paid >= due - eps && due > 0) status = 'payment_completed';
    else if (paid >= due - eps && due <= 0) status = 'payment_completed';
    else if (paid > eps) status = 'half_paid';
    return { totalPaid: paid, balance: bal, paymentStatus: status };
  }, [paymentRows, totals.total]);

  const paymentRowsForPrint = useMemo(
    () => paymentRows.filter((r) => Number.parseFloat(r.amount) > 0),
    [paymentRows],
  );

  const duePositive = (Number(totals.total) || 0) > 0.005;
  const statusBanner = useMemo(() => {
    if (paymentStatus === 'payment_completed') {
      return { bg: '#dbe6cf', color: '#1f7a24', title: 'PAYMENT COMPLETED', subtitle: null };
    }
    if (paymentStatus === 'half_paid') {
      return {
        bg: '#fef3c7',
        color: '#92400e',
        title: 'HALF PAID',
        subtitle: `Balance due: ${formatRM(balance)}`,
      };
    }
    return {
      bg: '#fee2e2',
      color: '#991b1b',
      title: 'NOT PAID',
      subtitle: duePositive ? `Amount due: ${formatRM(totals.total)}` : null,
    };
  }, [paymentStatus, balance, totals.total, duePositive]);

  const handleDownloadPdf = useCallback(async () => {
    const root = rootRef.current;
    if (!root || downloadLock.current || !collection) return;
    const pages = root.querySelectorAll('.receipt-page');
    if (!pages.length) return;
    downloadLock.current = true;
    setDownloadBusy(true);
    try {
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      for (let i = 0; i < pages.length; i += 1) {
        const el = pages[i];
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          ignoreElements: shouldIgnoreReceiptExportNode,
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
      }
      pdf.save(`${receiptPdfDownloadBase(collection)}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Could not create PDF. Try Print (A4) and choose Save as PDF instead.');
    } finally {
      downloadLock.current = false;
      setDownloadBusy(false);
    }
  }, [collection]);

  const buildSavePayload = useCallback(() => {
    const receiptPayments = paymentRows
      .map((r) => ({
        paymentDate: r.paymentDate ? new Date(`${r.paymentDate}T12:00:00`).toISOString() : new Date().toISOString(),
        paymentMethod: (r.paymentMethod || 'Cash').trim(),
        amount: Number.parseFloat(r.amount) || 0,
        reference: (r.reference || '').trim(),
      }))
      .filter((r) => r.amount > 0);
    return {
      receiptIssuedDate: receiptIssuedDate
        ? new Date(`${receiptIssuedDate}T12:00:00`).toISOString()
        : new Date().toISOString(),
      receiptPayments,
    };
  }, [paymentRows, receiptIssuedDate]);

  const updatePaymentRow = (idx, field, value) => {
    setPaymentRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addPaymentRow = () => setPaymentRows((prev) => [...prev, emptyPaymentRow()]);
  const removePaymentRow = (idx) => {
    setPaymentRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSave = async () => {
    if (!onSaveReceipt || saveBusy) return;
    setSaveBusy(true);
    try {
      await onSaveReceipt(buildSavePayload());
    } catch (e) {
      console.error(e);
    } finally {
      setSaveBusy(false);
    }
  };

  const emptyRows = Math.max(14 - rows.length, 6);

  if (!collection) {
    return (
      <div className="receipt-root receipt-hide-print" style={{ padding: '24px', background: '#f3f4f6' }}>
        <p style={{ marginBottom: '12px' }}>No collection loaded.</p>
        <button type="button" onClick={onClose} style={{ border: 'none', background: '#374151', color: '#fff', padding: '8px 14px', cursor: 'pointer' }}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="receipt-root" style={{ backgroundColor: '#f3f4f6', padding: '8px 0' }}>
      <style>
        {`
          .receipt-page {
            width: ${A4_WIDTH};
            min-height: ${A4_HEIGHT};
            margin: 0 auto;
            background: #fff;
            padding: 9mm 8mm 8mm;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            color: #1f2937;
            font-size: 12px;
            display: flex;
            flex-direction: column;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
            }
            body * {
              visibility: hidden;
            }
            .receipt-root,
            .receipt-root * {
              visibility: visible;
            }
            .receipt-root {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              background: #fff !important;
            }
            @page { size: A4; margin: 0; }
            .receipt-root { padding: 0 !important; background: #fff !important; }
            .receipt-page {
              width: ${A4_WIDTH} !important;
              min-height: ${A4_HEIGHT} !important;
              margin: 0 !important;
            }
            .receipt-hide-print,
            .receipt-payment-editor-only { display: none !important; }
          }
        `}
      </style>

      <div className="receipt-page">
        <div className="receipt-hide-print" style={{ textAlign: 'right', marginBottom: '10px' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: '#374151', color: '#fff', padding: '8px 14px', cursor: 'pointer' }}
          >
            Close
          </button>
          {typeof onPrint === 'function' && (
            <button
              type="button"
              onClick={onPrint}
              style={{ border: 'none', background: '#111827', color: '#fff', padding: '8px 14px', marginLeft: '8px', cursor: 'pointer' }}
            >
              Print (A4)
            </button>
          )}
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={downloadBusy}
            style={{
              border: 'none',
              background: downloadBusy ? '#9ca3af' : 'var(--jet-royal, #0056f1)',
              color: '#fff',
              padding: '8px 14px',
              marginLeft: '8px',
              cursor: downloadBusy ? 'wait' : 'pointer',
            }}
          >
            {downloadBusy ? 'Preparing…' : 'Download PDF (A4)'}
          </button>
          {onSaveReceipt && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saveBusy}
              style={{
                border: 'none',
                background: saveBusy ? '#9ca3af' : '#047857',
                color: '#fff',
                padding: '8px 14px',
                marginLeft: '8px',
                cursor: saveBusy ? 'wait' : 'pointer',
              }}
            >
              {saveBusy ? 'Saving…' : 'Save payments'}
            </button>
          )}
        </div>

        <div className="receipt-hide-print receipt-payment-editor-only jet-receipt-payment-panel">
          <div className="jet-receipt-payment-panel__title">Receipt &amp; payments (manual)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
            <label className="jet-receipt-payment-field">
              <span>Receipt date</span>
              <input type="date" className="form-input" value={receiptIssuedDate} onChange={(e) => setReceiptIssuedDate(e.target.value)} />
            </label>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #d1d5db' }}>
                  <th style={{ padding: '6px 4px' }}>Payment date</th>
                  <th style={{ padding: '6px 4px' }}>Method</th>
                  <th style={{ padding: '6px 4px' }}>Amount (RM)</th>
                  <th style={{ padding: '6px 4px', width: '72px' }} />
                </tr>
              </thead>
              <tbody>
                {paymentRows.map((r, idx) => (
                  <tr key={`pay-${idx}`}>
                    <td style={{ padding: '4px' }}>
                      <input type="date" value={r.paymentDate} onChange={(e) => updatePaymentRow(idx, 'paymentDate', e.target.value)} />
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select
                        value={PAYMENT_METHODS.includes(r.paymentMethod) ? r.paymentMethod : 'Other'}
                        onChange={(e) => updatePaymentRow(idx, 'paymentMethod', e.target.value)}
                      >
                        {PAYMENT_METHODS.map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        style={{ width: '100px' }}
                        value={r.amount}
                        onChange={(e) => updatePaymentRow(idx, 'amount', e.target.value)}
                      />
                    </td>
                    <td style={{ padding: '4px' }}>
                      <button type="button" onClick={() => removePaymentRow(idx)} disabled={paymentRows.length <= 1}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="jet-receipt-payment-panel__btn-add" onClick={addPaymentRow} style={{ marginTop: '8px' }}>+ Add payment</button>
          </div>
          <div className="jet-receipt-payment-panel__meta">
            Total due: <strong>{formatRM(totals.total)}</strong>
            {' · '}
            Paid: <strong>{formatRM(totalPaid)}</strong>
            {paymentStatus === 'half_paid' || paymentStatus === 'not_paid' ? (
              <>
                {' · '}
                Balance: <strong>{formatRM(balance)}</strong>
              </>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', paddingBottom: '2px' }}>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <img
              src={logoSrc}
              alt="Jet Shipping Logo"
              style={{ width: '96px', height: '96px', objectFit: 'contain', display: 'block' }}
              onError={() => {
                if (!logoTriedFallback.current) {
                  logoTriedFallback.current = true;
                  setLogoSrc('/images/logo/jet-shipping-logo.png');
                }
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: '2px' }}>
            <div
              style={{
                color: RED,
                fontWeight: 800,
                fontSize: '30px',
                lineHeight: 1.15,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              JET SHIPPING CARGO - MALAYSIA
            </div>
            <div style={{ marginTop: '12px', paddingTop: '14px', borderTop: '1px solid #e5e7eb' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#4b5563', lineHeight: 1.6 }}>
                No.AZ-B1-07, Lot No.1B/2(A), Anzen Business Park, Jalan 4/37A, Kawasan Industri Taman Bukit
                <br />
                Maluri, Kepong, Kuala Lumpur, 52100
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#374151' }}>
                <span style={{ color: '#9ca3af', fontWeight: 700, fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Email</span>
                <span style={{ margin: '0 6px', color: '#d1d5db' }}>|</span>
                <span style={{ color: BLUE, fontWeight: 600 }}>jetshippingandlogistic@gmail.com</span>
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', height: '4px', marginTop: '14px', borderRadius: '1px', overflow: 'hidden' }}>
          <div style={{ width: '100px', backgroundColor: RED, flexShrink: 0 }} />
          <div style={{ flex: 1, backgroundColor: BLUE }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '14px' }}>
          <div>
            <div style={{ color: RED, fontWeight: 800, fontSize: '22px', marginBottom: '8px', letterSpacing: '0.02em' }}>RECEIPT</div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: '5px' }}>
              <div>Receipt</div><div>{receiptNo(collection)}</div>
              <div>Receipt Date</div><div>{receiptIssuedDate}</div>
              <div>Collection No</div><div>{collection?.collectionNo || 'N/A'}</div>
              <div>Clearing Type</div><div>{(collection?.clearingType || 'N/A').toUpperCase()}</div>
            </div>
          </div>
          <div>
            <div style={{ color: RED, fontWeight: 800, fontSize: '22px', marginBottom: '8px', letterSpacing: '0.02em' }}>RECEIPT TO</div>
            <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', rowGap: '5px' }}>
              <div>Name</div><div>{receiptTo.nameWithInitials || 'N/A'}</div>
              <div>Passport / ID</div><div>{receiptTo.passportNo || receiptTo.nicNo || collection?.shipperPassportNo || 'N/A'}</div>
              <div>Contact</div><div>{normalizeContact(receiptTo)}</div>
              <div>Address</div><div>{receiptTo.address || 'N/A'}</div>
            </div>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ color: '#fff', fontWeight: 700 }}>
              <th style={{ width: '52px', backgroundColor: BLUE, border: `1px solid ${BLUE}`, padding: '6px' }}>NO</th>
              <th style={{ width: '172px', backgroundColor: BLUE, border: `1px solid ${BLUE}`, padding: '6px', textAlign: 'left' }}>ITEM TYPE</th>
              <th style={{ backgroundColor: BLUE, border: `1px solid ${BLUE}`, padding: '6px', textAlign: 'left' }}>DESCRIPTION</th>
              <th style={{ width: '72px', backgroundColor: BLUE, border: `1px solid ${BLUE}`, padding: '6px' }}>QTY</th>
              <th style={{ width: '102px', backgroundColor: RED, border: `1px solid ${RED}`, padding: '6px', textAlign: 'right' }}>PRICE</th>
              <th style={{ width: '102px', backgroundColor: RED, border: `1px solid ${RED}`, padding: '6px', textAlign: 'right' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`row-${row.no}`}>
                <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'center', height: '25px' }}>{row.no}</td>
                <td style={{ borderBottom: '1px solid #d1d5db', padding: '0 8px', height: '25px', wordBreak: 'break-word' }}>{row.itemType}</td>
                <td style={{ borderBottom: '1px solid #d1d5db', padding: '0 8px', height: '25px' }}>{row.description}</td>
                <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'center', height: '25px' }}>{row.qty}</td>
                <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'right', paddingRight: '8px', height: '25px' }}>{row.price.toFixed(2)}</td>
                <td style={{ borderBottom: '1px solid #d1d5db', textAlign: 'right', paddingRight: '8px', height: '25px' }}>{row.total.toFixed(2)}</td>
              </tr>
            ))}
            {Array.from({ length: emptyRows }).map((_, idx) => (
              <tr key={`empty-${idx}`}>
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
                <td style={{ borderBottom: '1px solid #d1d5db', height: '25px' }} />
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ borderTop: `4px solid ${BLUE}`, marginTop: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', marginTop: '10px', gap: '26px' }}>
          <div>
            <div style={{ color: BLUE, fontWeight: 700, fontSize: '25px', marginBottom: '6px' }}>Payment Details</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', paddingBottom: '4px' }}>Method</th>
                  <th style={{ textAlign: 'left', paddingBottom: '4px' }}>Date</th>
                  <th style={{ textAlign: 'right', paddingBottom: '4px' }}>Amount (RM)</th>
                </tr>
              </thead>
              <tbody>
                {paymentRowsForPrint.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: '6px 0', color: '#6b7280', fontStyle: 'italic' }}>No payments recorded</td>
                  </tr>
                ) : (
                  paymentRowsForPrint.map((r, i) => (
                    <tr key={`print-pay-${i}`}>
                      <td>{r.paymentMethod || '—'}</td>
                      <td>{r.paymentDate || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{(Number.parseFloat(r.amount) || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
                <tr>
                  <td colSpan={2} style={{ fontWeight: 700, paddingTop: '6px' }}>Total paid</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, paddingTop: '6px' }}>{totalPaid.toFixed(2)}</td>
                </tr>
                {paymentStatus !== 'payment_completed' && (
                  <tr>
                    <td colSpan={2} style={{ fontWeight: 700 }}>Balance due</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{balance.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div
              style={{
                marginTop: '8px',
                backgroundColor: statusBanner.bg,
                color: statusBanner.color,
                fontWeight: 800,
                textAlign: 'center',
                padding: '5px 8px',
              }}
            >
              {statusBanner.title}
              {statusBanner.subtitle ? (
                <div style={{ fontWeight: 600, fontSize: '11px', marginTop: '4px' }}>{statusBanner.subtitle}</div>
              ) : null}
            </div>
          </div>

          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0' }}>Sub Total</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatRM(totals.subTotal)}</td></tr>
                <tr><td style={{ padding: '4px 0' }}>Packaging Charge</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatRM(totals.packagingCharge)}</td></tr>
                <tr><td style={{ padding: '4px 0' }}>Transport</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatRM(totals.transport)}</td></tr>
                <tr><td style={{ padding: '4px 0' }}>Other Charges</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{formatRM(totals.otherCharge)}</td></tr>
                <tr><td style={{ padding: '4px 0' }}>Discount</td><td style={{ textAlign: 'right', fontWeight: 700 }}>{totals.discountLabel}</td></tr>
                <tr><td style={{ paddingTop: '10px', fontWeight: 800 }}>TOTAL</td><td style={{ textAlign: 'right', color: RED, fontWeight: 800, fontSize: '22px', paddingTop: '10px' }}>{formatRM(totals.total)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            marginTop: '28px',
            padding: '10px 12px',
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#374151',
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          This is a system-generated document. No signature is required.
        </div>

        <div style={{ marginTop: 'auto' }} />
        <div style={{ borderTop: `4px solid ${BLUE}`, marginTop: '12px', paddingTop: '6px' }}>Terms & Conditions Apply</div>
        <div style={{ marginTop: '7px', backgroundColor: RED, color: '#fff', textAlign: 'center', padding: '8px 10px', fontWeight: 700, fontSize: '11px' }}>
          WHATSAPP:+60 11-2119 0765 | OFFICE:+60 3-6262 0505 | HOTLINE:+60 11-6053 6205
        </div>
      </div>
    </div>
  );
};

export default Receipt;
